import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { analytics } from '@features/analytics';
import {
  useRoomUpgradeDialogLabels,
  useRoomUpgradeOffer,
} from '@features/List/BookableList/UpgradeItem/v2/useRoomUpgradeOffer';
import { TranslationKey, useTranslations } from '@lang';
import { queryParamToString } from '@utils/queryParam';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'shared-routing';
import { Item } from 'shared-types';
import { formatCurrency } from 'voyager';
import type { BasketTemplateProps, BasketUpgradeOffer } from 'voyager';
import { BASKET_ROOM_UPGRADE_FLAG, UPGRADE_QUERY_PARAM } from './constants';
import { BasketRoomUpgradeOffer, useBasketRoomUpgradeOffers } from './useBasketRoomUpgradeOffers';
import { UpgradeOutcome, useUpgradeBasketRoom } from './useUpgradeBasketRoom';

type ListLocation = 'basket_page' | 'basket_drawer';

const OUTCOME_MESSAGE_KEYS: Record<Exclude<UpgradeOutcome, 'upgraded'>, TranslationKey> = {
  unavailable: 'basket.upgrade.unavailable',
  changed: 'basket.upgrade.changed',
  failed: 'basket.upgrade.failed',
  originalNotRemoved: 'basket.upgrade.originalNotRemoved',
};

const toTrackedItem = ({ item, upgrade }: BasketRoomUpgradeOffer) => ({
  original_item_id: String(item.bookable?.ids?.roomId ?? item.id),
  original_item_name: item.bookable?.name ?? item.name,
  item_id: upgrade.id,
  item_name: upgrade.name,
  price_difference: upgrade.priceDifference,
  price_per_stay: upgrade.pricePerStay,
});

const useTrackOffersShown = (
  offers: BasketRoomUpgradeOffer[],
  listLocation: ListLocation,
  isVisible: boolean
) => {
  const tracked = useRef(new Set<string>());
  useEffect(() => {
    if (!isVisible) return;
    const fresh = offers.filter(({ item }) => !tracked.current.has(String(item.id)));
    if (!fresh.length) return;
    fresh.forEach(({ item }) => tracked.current.add(String(item.id)));
    analytics.track({
      event: 'upgrade_offered',
      params: {
        upgrade_source: 'basket',
        list_location: listLocation,
        items: fresh.map(toTrackedItem),
      },
    });
  }, [offers, listLocation, isVisible]);
};

const useUpgradeOfferCards = (
  offers: BasketRoomUpgradeOffer[],
  onSelect: (itemId: string) => void,
  locale?: string
): BasketUpgradeOffer[] => {
  const { t } = useTranslations();
  return offers.map(({ item, upgrade }) => {
    const itemId = String(item.id);
    const { amount, currencyCode } = upgrade.priceDifference;
    return {
      itemId,
      message: t('basket.upgrade.offer', {
        price: formatCurrency(amount, currencyCode, { locale }),
      }),
      actionAriaLabel: t('basket.upgrade.seeFor', { room: item.bookable?.name ?? item.name }),
      onSelect: () => onSelect(itemId),
    };
  });
};

export const useBasketDrawerRoomUpgrade = (
  items: Item[] | undefined,
  basketRoute: string,
  isVisible: boolean,
  locale?: string
) => {
  const router = useRouter();
  const isEnabled = useFeatureIsOn(BASKET_ROOM_UPGRADE_FLAG);
  const offers = useBasketRoomUpgradeOffers(Array.isArray(items) ? items : [], isEnabled);
  useTrackOffersShown(offers, 'basket_drawer', isVisible);

  return useUpgradeOfferCards(
    offers,
    (itemId) => router.push({ pathname: basketRoute, query: { [UPGRADE_QUERY_PARAM]: itemId } }),
    locale
  );
};

export const useBasketPageRoomUpgrade = (
  items: Item[] | undefined,
  language: string,
  isVisible: boolean
): Pick<BasketTemplateProps, 'upgradeOffers' | 'upgradeDialog'> => {
  const { t } = useTranslations();
  const router = useRouter();
  const isEnabled = useFeatureIsOn(BASKET_ROOM_UPGRADE_FLAG);
  const offers = useBasketRoomUpgradeOffers(Array.isArray(items) ? items : [], isEnabled);
  const { upgradeRoom, isUpgrading } = useUpgradeBasketRoom(language);
  const dialogLabels = useRoomUpgradeDialogLabels();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [attempted, setAttempted] = useState<BasketRoomUpgradeOffer>();
  useTrackOffersShown(offers, 'basket_page', isVisible);

  const selectedItemId = queryParamToString(router.query[UPGRADE_QUERY_PARAM]);
  const selected = useMemo(
    () =>
      offers.find(({ item }) => String(item.id) === selectedItemId) ??
      (attempted && String(attempted.item.id) === selectedItemId ? attempted : undefined),
    [offers, selectedItemId, attempted]
  );
  const upgradeRoomOffer = useRoomUpgradeOffer(selected?.upgrade);

  const setSelectedItem = (itemId?: string) => {
    const { [UPGRADE_QUERY_PARAM]: _upgrade, ...query } = router.query;
    setErrorMessage(undefined);
    setAttempted(undefined);
    router.replace(
      {
        pathname: router.pathname,
        query: itemId ? { ...query, [UPGRADE_QUERY_PARAM]: itemId } : query,
      },
      undefined,
      { shallow: true }
    );
  };

  const trackSelection = (offer: BasketRoomUpgradeOffer, accepted: boolean) =>
    analytics.track({
      event: 'upgrade_selection',
      params: { upgrade_source: 'basket', items: [{ ...toTrackedItem(offer), accepted }] },
    });

  const upgradeOffers = useUpgradeOfferCards(offers, setSelectedItem, language);

  if (!selected || !upgradeRoomOffer) return { upgradeOffers };

  return {
    upgradeOffers,
    upgradeDialog: {
      open: true,
      upgradeRoom: upgradeRoomOffer,
      headlinePrice: 'difference',
      upgrading: isUpgrading,
      errorMessage,
      labels: dialogLabels,
      onClose: () => setSelectedItem(),
      onSkip: () => {
        trackSelection(selected, false);
        setSelectedItem();
      },
      onUpgrade: async () => {
        trackSelection(selected, true);
        setErrorMessage(undefined);
        setAttempted(selected);
        const outcome = await upgradeRoom(selected.item, selected.upgrade);
        if (outcome === 'upgraded') {
          setSelectedItem();
          return;
        }
        setErrorMessage(t(OUTCOME_MESSAGE_KEYS[outcome]));
      },
    },
  };
};
